// 鶏 (inverted-jester・終わらぬ鬨) の一戦 (2026-10-29 から配布版・通しの道のりも含む全部)。
//   作者の答え (10-29): 技は5本・2幕・終盤なので難しめ・鶏専用のとどめ・遊べる所までできたらすぐ配布版を替える。
//   前の道化の版は退避した (戻し方＝docs/research/2026-10-29-rooster-fight-to-release.md)。
//
//   絵 = src/rooster_crow_art.ts。技 (連 = def.strings・1本の技も1拍の連):
//     1幕 = 鬨ずらし 短い鳴き・長い鳴き (鳴き止みを見て爪の3発を弾く)・暁呼び (宙から羽の雨・降りて翼打ち)。
//     2幕 = 1幕の3本 ＋ 宙廻り (宙を左右へ → 突っ込み → 走って爪)・鳴き挟み (爪2発 → 鳴いて真上へ → 落ちる踏みつけ)。
//   縦の2技の台本 = src/rooster_vertical.ts。飛んで裏へ回る (blink) は花嫁の瞬間移動の流用なので外す。
//   爪の当たりは描いた爪に合わせる (前端 = 爪の前端 67px + 15・花嫁と同じ線＝10-05 作者「A 絵に合わせる」)。
//     既定の collisionDraft は range 140 まで当たり、爪より約 70px 前で殴られていた (10-29 撮影で測った)。
//   爪の突っ込みは跳ぶ (高さ = src/rooster_claw_hop.ts・10-30 に 22→56px)。同じ爪の絵のまま床を 200px ほど滑って見えたため (10-29 撮影)。鶏の蹴爪は跳んで蹴る。
//   弾くほど速くなる (src/rooster_rush.ts・案B) は鬨ずらしの間と戻りだけに効かせる (鳴きの長さは読みの合図なので縮めない)。
//   弾いた時にボスへ溜まる体幹は ROOSTER_PARRY_POSTURE_MUL 倍 (10-29 検収 R-5 の答え「B 弾いた時の体幹の溜まりを減らす」)。
//     弾くだけ・一度も殴らずに 8/8 勝っていた (40〜46 秒) ため。前の量＝開発版 `&roosterParryMul=1`。
import { ATTACKS } from './attacks'
import { BOSS_DEFINITIONS } from './bosses'
import { setRoosterClawHop } from './rooster_claw_hop'
import { setRoosterCrowArt } from './rooster_crow_art'
import { FRENZY_VARIANTS } from './rooster_frenzy_spec'
import type { BossString } from './types'

const BOSS_ID = 'inverted-jester'
export const ROOSTER_FIGHT_ROOM1 = ['tokiZurashiShort', 'tokiZurashiLong', 'roosterDawnCall'] as const
export const ROOSTER_FIGHT_ROOM2 = ['roosterAerialRoam', 'roosterClawPair', 'roosterCrowDrop'] as const
/** 連 (幕ごと)。鳴き挟みは爪2発のすぐ後 (120ms) に鳴いて上がる。 */
export const ROOSTER_FIGHT_STRINGS: BossString[] = [
  { id: 'rooster_tokiShort', beats: [{ attackId: 'tokiZurashiShort' }], weight: 1, allowRepeat: true },
  { id: 'rooster_tokiLong', beats: [{ attackId: 'tokiZurashiLong' }], weight: 1, allowRepeat: true },
  { id: 'rooster_dawnCall', beats: [{ attackId: 'roosterDawnCall' }], weight: 0.7 },
  { id: 'rooster_aerialRoam', beats: [{ attackId: 'roosterAerialRoam' }], weight: 0.9, unlockAtExecutes: 1 },
  { id: 'rooster_crowBreak', beats: [{ attackId: 'roosterClawPair' }, { attackId: 'roosterCrowDrop', gapMs: 120 }], weight: 1, unlockAtExecutes: 1 },
]
/** 爪の当たり (ボスの中心から前へ。後ろは花嫁と同じ 60)。 */
export const ROOSTER_CLAW_STRIKE = { localFromPx: -60, localToPx: 82 } as const
const CLAW_ATTACKS = ['tokiZurashiShort', 'tokiZurashiLong', 'roosterAerialRoam', 'roosterClawPair'] as const
/** 爪の1発ごとに時間で跳ぶ技 (10-30 R-2)。宙廻りと爪の対は宙を使うので、踏み込んだ距離で跳ぶ形のまま。 */
const TOKI_HOP_BY_TIME = ['tokiZurashiShort', 'tokiZurashiLong']
/** 体幹の溜まりを減らす技 (爪と縦の2技)。 */
const PARRY_MUL_ATTACKS = [...ROOSTER_FIGHT_ROOM1, ...ROOSTER_FIGHT_ROOM2]
export const ROOSTER_PARRY_POSTURE_MUL = 0.5
let parryMul: number = ROOSTER_PARRY_POSTURE_MUL
/** `?roosterParryMul=` (0〜1)。数でなければ既定のまま。 */
export function setRoosterParryPostureMul(v: string | number | null): void {
  const n = typeof v === 'number' ? v : v === null ? NaN : Number(v)
  parryMul = Number.isFinite(n) && n >= 0 && n <= 1 ? n : ROOSTER_PARRY_POSTURE_MUL
}

type Def = (typeof BOSS_DEFINITIONS)[number]
type Atk = (typeof ATTACKS)[string]
let snap: { base: string[]; unlock: Def['unlockByExecutes']; blink: Def['blink']; strings: Def['strings']; claw: Record<string, Pick<Atk, 'strike' | 'approachDistancePx' | 'parryPostureMul'>> } | null = null

/** 鶏の一戦にする (起動時・preloadBossSprites より前に呼ぶ)。 */
export function useRoosterFight(): void {
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  if (!snap) snap = { base: [...def.baseAttackIds], unlock: def.unlockByExecutes?.map(u => ({ ...u, ids: [...u.ids] })), blink: def.blink,
    strings: def.strings, claw: Object.fromEntries(PARRY_MUL_ATTACKS.map(id => [id, { strike: ATTACKS[id].strike, approachDistancePx: ATTACKS[id].approachDistancePx, parryPostureMul: ATTACKS[id].parryPostureMul }])) }
  setRoosterCrowArt('crow', true)
  def.baseAttackIds = [...ROOSTER_FIGHT_ROOM1]
  def.unlockByExecutes = [
    { atExecutes: 0, ids: [...ROOSTER_FIGHT_ROOM1] },
    { atExecutes: 1, ids: [...ROOSTER_FIGHT_ROOM2] },
  ]
  def.blink = undefined
  def.strings = ROOSTER_FIGHT_STRINGS.map(s => ({ ...s, beats: s.beats.map(b => ({ ...b })) }))
  for (const id of CLAW_ATTACKS) {
    ATTACKS[id].strike = { ...ROOSTER_CLAW_STRIKE }
    ATTACKS[id].approachDistancePx = ROOSTER_CLAW_STRIKE.localToPx
  }
  for (const id of PARRY_MUL_ATTACKS) ATTACKS[id].parryPostureMul = parryMul
  // 焦り狂いの段0・段1 の鬨ずらし (配布版・src/rooster_frenzy.ts) も1発ごとに跳ぶ。技は初めて要る時に登録される。
  const frenzyIds = FRENZY_VARIANTS.map(v => v.id)
  setRoosterClawHop([...CLAW_ATTACKS, ...frenzyIds], undefined, [...TOKI_HOP_BY_TIME, ...frenzyIds])
}

/** 外す (テストの後始末用。絵の差し替えは戻さない)。 */
export function restoreRoosterFight(): void {
  if (!snap) return
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = snap.base
  def.unlockByExecutes = snap.unlock
  def.blink = snap.blink
  if (snap.strings) def.strings = snap.strings
  else delete def.strings
  for (const [id, v] of Object.entries(snap.claw)) { ATTACKS[id].strike = v.strike; ATTACKS[id].approachDistancePx = v.approachDistancePx; ATTACKS[id].parryPostureMul = v.parryPostureMul }
  snap = null
  setRoosterClawHop([])
}
